"use client"

import { useEffect, useRef, type ReactNode } from "react"
import { recordSummarizeWithClick } from "@/lib/blog/summarize-with-ai-analytics"

/**
 * FRIDGE-1311 — the only browser code in the "Summarize with" row. The links
 * themselves are server-rendered (components/blocks/article-summarize-with.tsx)
 * and arrive here as children; this wrapper listens on the row and measures
 * which assistant was pressed. A left click, Enter on a focused link and a
 * middle click all open the assistant, so all three are counted.
 */
export function ArticleSummarizeTracker({
  path,
  className,
  children,
}: {
  /** The article's canonical path, e.g. /blogs/news/0-7-cu-ft-mini-fridge. */
  path: string
  className?: string
  children: ReactNode
}) {
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const onPress = (event: MouseEvent) => {
      if (event.type === "auxclick" && event.button !== 1) return
      const link = event.target instanceof Element ? event.target.closest("a[data-ai-assistant]") : null
      const assistant = link?.getAttribute("data-ai-assistant")
      if (assistant) recordSummarizeWithClick(assistant, path)
    }
    row.addEventListener("click", onPress)
    row.addEventListener("auxclick", onPress)
    return () => {
      row.removeEventListener("click", onPress)
      row.removeEventListener("auxclick", onPress)
    }
  }, [path])

  return (
    <div ref={rowRef} className={className}>
      {children}
    </div>
  )
}
